import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Spinner, Text } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { Download } from '@factorialco/factorial-one-react/icons/app'
import { EnvironmentContext } from 'config/environment'
import { UniversalImporterValueObjectsImportTypeEnum } from 'generated/resources/datatable.graphql'
import { NuvoImporterDocument } from 'generated/resources/graphql'
import {
  type ColumnAPI,
  type DataHandlerResultValues,
  type Modifier,
  NuvoImporter as RawNuvoImporter,
  RejectSubmitResult,
} from 'nuvo-react'

import { Authorized } from 'modules/core/components/Authorized'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import useDownloadTemplate from 'modules/employees/hooks/useDownloadTemplate'
import { i18nOverrides } from 'modules/universal_importer/components/NuvoImporter/locales'
import { getStyle } from 'modules/universal_importer/components/NuvoImporter/styles'
import {
  type EditionData,
  Metadata,
} from 'modules/universal_importer/components/NuvoImporter/types'
import { useColumnsMapping } from 'modules/universal_importer/components/NuvoImporter/useColumnsMapping'
import { useEditionData } from 'modules/universal_importer/components/NuvoImporter/useEditionData'
import { useFileUpload } from 'modules/universal_importer/components/NuvoImporter/useFileUpload'
import { useGetMetadata } from 'modules/universal_importer/components/NuvoImporter/useGetMetadata'
import { useLanguage } from 'modules/universal_importer/components/NuvoImporter/useLanguage'
import { useNavigationHandling } from 'modules/universal_importer/components/NuvoImporter/useNavigationHandling'
import { useNuvoSession } from 'modules/universal_importer/components/NuvoImporter/useNuvoSession'
import { useSendImportResults } from 'modules/universal_importer/components/NuvoImporter/useSendingImportResults'

const downloadPath = (
  importer_type: UniversalImporterValueObjectsImportTypeEnum,
  metadata: Metadata = {}
) => {
  const queryParams = new URLSearchParams()
  queryParams.append('metadata', JSON.stringify(metadata))
  return `/universal_importer/templates/${importer_type}?${queryParams.toString()}`
}

type Props = {
  importType: UniversalImporterValueObjectsImportTypeEnum
  companyId: number
  metadata?: Metadata
  locale?: string
  editionData?: EditionData
  customBackNavigation?: string
  customFinishNavigation?: string
  isEdition?: boolean
  parentProcessId?: number | null
  rootProcessId?: number | null
}

const NuvoImporter = ({
  importType,
  companyId,
  metadata = {},
  locale = 'en-US',
  customBackNavigation,
  customFinishNavigation,
  isEdition,
  parentProcessId,
  rootProcessId,
}: Props) => {
  const { nuvoApiKey, nuvoDeveloperMode } = React.useContext(EnvironmentContext)
  const navigate = useNavigate()
  const { sendImportResults } = useSendImportResults()
  const {
    access: currentUser,
    company: { subscription },
  } = useCurrentSession()
  const [hasChanges, setHasChanges] = React.useState(false)
  const nuvoImporterRef = React.useRef<HTMLDivElement>(null)
  const { metadataInput, legalEntitiesIds } = useGetMetadata(metadata)
  const { data, loading } = useQuery(NuvoImporterDocument, {
    variables: { companyId, importType, metadata: metadataInput },
  })
  const { downloading, onDownload, error, value } = useDownloadTemplate({
    path: downloadPath(importType, metadata),
  })
  const [initialData, setInitialData] = React.useState<
    Record<string, string | string[]>[] | undefined
  >(undefined)
  const isDownloadingRef = React.useRef(false)
  const tracker = React.useMemo(
    () => ({
      id: '',
      refresh() {
        this.id = crypto.randomUUID()
      },
    }),
    []
  )
  const commonEventProperties = useUnsafeMemo(
    () => ({
      user_id: currentUser.id,
      company_id: companyId,
      legal_entity_ids: legalEntitiesIds,
      import_type: importType,
      tracker_id: tracker.id,
    }),
    [currentUser.id, companyId, legalEntitiesIds, importType, tracker.id]
  )
  const { parsedFile, parseFileLoading, handleFileUpload } = useFileUpload({
    onFileUpload: (file) => {
      track(trackingEvents.universalImporter.imports.attachFile, {
        file_name: file.name,
        file_size: file.size,
        file_type: file.type,
        ...commonEventProperties,
      })
    },
  })
  useNuvoSession({
    importType,
    parsedFile,
    initialData,
    loading,
    nuvoImporterRef,
  })
  const { bypassAlertNavigationConfirmation } = useNavigationHandling({
    isDownloadingRef,
    navigate,
    confirmExitMessage: i18n.t('universal_importer.nuvo_importer.confirm_exit'),
    customBackNavigation,
  })
  const { language } = useLanguage({ locale })
  const { columns } = useColumnsMapping({ data })
  const { editionData, loading: editionLoading } = useEditionData({
    companyId,
    importType,
    metadata: metadataInput,
    isEdition: !!isEdition,
  })

  const handleOnDownload = () => {
    isDownloadingRef.current = true
    onDownload()
    track(trackingEvents.universalImporter.imports.downloadTemplate, {
      error: error && value?.toString(),
      ...commonEventProperties,
    })
  }

  // Reset download state when downloading changes
  React.useEffect(() => {
    if (!downloading) {
      isDownloadingRef.current = false
    }
  }, [downloading])

  useUnsafeEffect(() => {
    if (!loading && isEdition) {
      setInitialData(editionData)
    }
  }, [editionData, loading, isEdition])

  if (loading || editionLoading)
    return (
      <Box justifyContent="center" alignItems="center" height="full">
        <Spinner />
      </Box>
    )

  return (
    <Box gap="s40" extra={{ 'data-intercom-target': 'universal-importer-nuvo-importer' }}>
      {!isEdition && (
        <>
          <Authorized subscription={subscription}>
            <DropzoneBox
              onDrop={(files: File[]) => handleFileUpload(files)}
              iconName="common-file-text-upload"
              message={
                parsedFile?.length && parsedFile[0]?.fileName
                  ? parsedFile[0].fileName
                  : i18n.t('universal_importer.nuvo_importer.upload_file.message')
              }
              description={i18n.t('universal_importer.nuvo_importer.upload_file.description')}
              multiple={false}
              accept={{
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
                'application/vnd.ms-excel': [],
              }}
              maxSize={4}
              disabled={parseFileLoading}
            />
          </Authorized>
          <Box flexDirection="column" alignItems="flexStart">
            <Text.Normal weight="semibold">
              {i18n.t('universal_importer.nuvo_importer.download_template.title')}
            </Text.Normal>
            <Text.Normal>
              {i18n.t('universal_importer.nuvo_importer.download_template.description')}
            </Text.Normal>
            <Box paddingTop="s8">
              <Button
                onClick={handleOnDownload}
                size="md"
                variant="neutral"
                label={i18n.t('universal_importer.nuvo_importer.download_template.link')}
                loading={downloading}
                icon={Download}
                data-intercom-target="universal-importer-nuvo-importer-download-template"
              />
            </Box>
          </Box>
        </>
      )}
      <Box ref={nuvoImporterRef}>
        <RawNuvoImporter
          licenseKey={nuvoApiKey}
          settings={{
            language,
            developerMode: nuvoDeveloperMode,
            identifier: importType,
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            columns: columns as ColumnAPI[],
            onlyMappedColumns: !isEdition,
            automaticHeaderDetection: true,
            disableSuccessModal: true,
            completeImportAction: 'block',
            smartTable: true,
            style: getStyle(isEdition, editionData?.length),
            i18nOverrides: i18nOverrides(!!isEdition),
          }}
          dataHandler={{
            headerStep: async () => {
              track(trackingEvents.universalImporter.imports.headerStep, commonEventProperties)
            },
            reviewStep: async (
              _modifier: Modifier,
              reviewData: DataHandlerResultValues
            ): Promise<DataHandlerResultValues> => {
              track(trackingEvents.universalImporter.imports.reviewStep, commonEventProperties)
              return reviewData
            },
          }}
          onCancel={() => {
            track(trackingEvents.universalImporter.imports.cancelled, commonEventProperties)
            tracker.refresh()
            bypassAlertNavigationConfirmation()
            if (customBackNavigation) {
              navigate(customBackNavigation)
            } else {
              navigate(-1)
            }
          }}
          onEntryChange={() => {
            if (isEdition && !hasChanges) {
              setHasChanges(true)
            }
          }}
          onResults={async (result, errors, complete) => {
            if (!result.length) {
              complete(
                new RejectSubmitResult(
                  i18n.t('universal_importer.nuvo_importer.results.no_rows_alert.title'),
                  i18n.t('universal_importer.nuvo_importer.results.no_rows_alert.description')
                )
              )
              return
            }

            track(trackingEvents.universalImporter.imports.mappingRequested, {
              total_rows: result.length,
              rows_with_errors: errors.length,
              ...commonEventProperties,
            })

            if (isEdition && !hasChanges) {
              complete(
                new RejectSubmitResult(
                  i18n.t('universal_importer.nuvo_importer.results.no_changes_alert.title'),
                  i18n.t('universal_importer.nuvo_importer.results.no_changes_alert.description')
                )
              )
              return
            }

            const response = await sendImportResults({
              data: result,
              metadata,
              companyId,
              importType,
              parentProcessId,
              rootProcessId,
            })

            if (response.success) {
              const columnNames = Object.keys(result?.[0] || {})

              const columnPercentages = columnNames
                .map((columnName) => {
                  const columnValues = result.map((row) => row[columnName])
                  const percentage =
                    (columnValues.filter(Boolean).length / columnValues.length) * 100
                  return { [columnName]: percentage.toFixed(2) }
                })
                .reduce((acc, curr) => ({ ...acc, ...curr }), {})

              track(trackingEvents.universalImporter.imports.started, {
                total_rows: result.length,
                ...commonEventProperties,
                import_process_id: response.value.id,
                column_percentages: JSON.stringify(columnPercentages),
              })

              bypassAlertNavigationConfirmation()
              if (customFinishNavigation) {
                navigate(customFinishNavigation)
              } else {
                navigate(-1)
              }
            } else {
              track(trackingEvents.universalImporter.imports.mappingRequestError, {
                total_rows: result.length,
                ...commonEventProperties,
              })
              complete(
                new RejectSubmitResult(
                  i18n.t('universal_importer.nuvo_importer.results.api_response_alert.title'),
                  i18n.t('universal_importer.nuvo_importer.results.api_response_alert.description')
                )
              )
            }
          }}
        />
      </Box>
    </Box>
  )
}

export default NuvoImporter
